import { Navigate, Route, Routes, useLocation  } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';

import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { ProtectedRoute } from '@/components/dashboard/ProtectedRoute';

import { UploadPage } from '@/pages/UploadPage';
import { PrintOptionsPage } from '@/pages/PrintOptionsPage';
import { PaymentPage } from '@/pages/PaymentPage';
import { OtpSuccessPage } from '@/pages/OtpSuccessPage';

import { LoginPage } from '@/pages/admin/LoginPage';
import { DashboardHomePage } from '@/pages/admin/DashboardHomePage';
import { VendorsPage } from '@/pages/admin/VendorsPage';
import { KiosksPage } from '@/pages/admin/KiosksPage';
import { RatesPage } from '@/pages/admin/RatesPage';
import { VendorDetailPage } from '@/pages/admin/VendorDetailPage';

function LegacyKioskRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * All routes live here. Adding a new step page means: create it under
 * src/pages/<Name>Page/, add its path to ROUTES, and register it below —
 * nothing else in the app needs to change.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={ROUTES.admin.login} replace />} />

      {/* Kiosk flow: each page renders its own <AppLayout>; opened via scanner at /kiosk/upload */}
      {/* Old QR codes pointed at /upload?token=...; keep them working and carry the query string over */}
      {['upload', 'options', 'payment', 'success'].map((p) => (
        <Route key={p} path={`/${p}`} element={<LegacyKioskRedirect to={`/kiosk/${p}`} />} />
      ))}
      <Route path={ROUTES.upload} element={<UploadPage />} />
      <Route path={ROUTES.options} element={<PrintOptionsPage />} />
      <Route path={ROUTES.payment} element={<PaymentPage />} />
      <Route path={ROUTES.success} element={<OtpSuccessPage />} />

      {/* Admin login: no layout */}
      <Route path={ROUTES.admin.login} element={<LoginPage />} />

      {/* Admin dashboard: protected, uses the dashboard layout */}
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path={ROUTES.admin.root} element={<Navigate to={ROUTES.admin.dashboard} replace />} />
          <Route path={ROUTES.admin.dashboard} element={<DashboardHomePage />} />
          <Route path={ROUTES.admin.vendors} element={<VendorsPage />} />
          <Route path={`${ROUTES.admin.vendors}/:vendorId`} element={<VendorDetailPage />} />
          <Route path={ROUTES.admin.kiosks} element={<KiosksPage />} />
          <Route path={ROUTES.admin.rates} element={<RatesPage />} />
          {/* Aliases: the sidebar link comes from the backend Tab.path, which may be spelled differently */}
          {['rate', 'rate-charges', 'rate-charge', 'rate-card', 'rate-cards', 'pricing'].map((p) => (
            <Route key={p} path={`${ROUTES.admin.root}/${p}`} element={<RatesPage />} />
          ))}
          <Route path={`${ROUTES.admin.root}/*`} element={<h2 className="h4">This page is not built yet</h2>} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.admin.login} replace />} />
    </Routes>
  );
}